// Pure surface for the site-first intake. No supabase client, no state, no effects —
// so every one of these renders under renderToStaticMarkup in a node test, which is the
// only way the COPY gets checked (TCSD-0097). The shell is sop-create-modal.tsx.

import type { ReactNode } from 'react';
import { Check, Plus, AlertTriangle } from 'lucide-react';
import type { StepRailItem } from './intake-steps';

export const INTAKE_INPUT_CLS =
  'w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm text-tcs-text ' +
  'placeholder:text-tcs-text-faint focus:border-tcs-border-strong focus:outline-none';

// The create modal's own input + label shells. They live here rather than in
// sop-create-modal.tsx because the service-line editor is now shared between
// the create form and the account card (2026-09-08) and both must render the
// same control — a second copy is how two editors drift apart.
export const INPUT_CLS =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

export function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 inline-flex items-center gap-1 font-medium text-tcs-text">
        {label}
        {required && <span aria-hidden="true" className="text-rag-red">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-tcs-text-secondary">{hint}</span>}
    </label>
  );
}

/* ------------------------------------------------------------------ step rail */

export function StepRail({ items }: { items: StepRailItem[] }) {
  return (
    <ol className="flex items-center gap-0 border-b border-tcs-border bg-tcs-surface px-4 py-3">
      {items.map((it, i) => (
        <li key={it.step} className="flex flex-1 items-center last:flex-none">
          <span
            className={`flex items-center gap-2 text-xs ${
              it.state === 'on'
                ? 'font-semibold text-tcs-text'
                : it.state === 'done'
                  ? 'text-tcs-text-muted'
                  : 'text-tcs-text-faint'
            }`}
          >
            <span
              className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border text-[10px] font-semibold ${
                it.state === 'on'
                  ? 'border-tcs-text bg-tcs-text text-tcs-bg'
                  : it.state === 'done'
                    ? 'border-rag-green/40 bg-rag-green/10 text-rag-green'
                    : 'border-tcs-border-strong bg-tcs-bg text-tcs-text-faint'
              }`}
            >
              {it.state === 'done' ? <Check className="h-3 w-3" /> : i + 1}
            </span>
            <span className="hidden sm:inline">{it.label}</span>
          </span>
          {i < items.length - 1 && (
            <span className="mx-3 h-px flex-1 bg-tcs-border-strong" aria-hidden="true" />
          )}
        </li>
      ))}
    </ol>
  );
}

/* ------------------------------------------------- the (i) affordance on a field */

export function FieldHelp({
  body,
  good,
  bad,
}: {
  body: string;
  good: readonly string[];
  bad: readonly { text: string; why: string }[];
}) {
  return (
    <details className="mt-1.5 group">
      <summary className="inline-flex cursor-help list-none items-center gap-1 text-xs text-tcs-text-muted hover:text-tcs-text">
        <span className="grid h-4 w-4 place-items-center rounded-full border border-tcs-border-strong bg-tcs-surface text-[9px] font-bold">
          i
        </span>
        What goes here?
      </summary>
      <div className="mt-2 rounded-md border border-tcs-border bg-tcs-surface p-3 text-xs leading-relaxed text-tcs-text-muted">
        <p className="m-0">{body}</p>
        <p className="m-0 mt-2 border-t border-tcs-border pt-2">
          {good.map((g) => (
            <span key={g} className="mr-3 inline-block">
              <span className="font-semibold text-rag-green">✓</span> {g}
            </span>
          ))}
        </p>
        <p className="m-0 mt-1">
          {bad.map((b) => (
            <span key={b.text} className="mr-3 inline-block">
              <span className="font-semibold text-rag-red">✗</span> {b.text}{' '}
              <span className="text-tcs-text-faint">({b.why})</span>
            </span>
          ))}
        </p>
      </div>
    </details>
  );
}

export function IntakeField({
  label,
  required,
  ask,
  children,
  help,
}: {
  label: string;
  required?: boolean;
  ask?: string;
  children: ReactNode;
  help?: ReactNode;
}) {
  return (
    <div className="mb-4">
      <label className="mb-0.5 flex items-baseline gap-1.5 text-sm font-semibold text-tcs-text">
        {label}
        {required && <span className="text-xs font-normal text-rag-red">required</span>}
      </label>
      {ask && <p className="mb-1.5 mt-0 text-xs text-tcs-text-muted">{ask}</p>}
      {children}
      {help}
    </div>
  );
}

/* --------------------------------------------------------- customer search rows */

export type CustomerMatch = {
  account_id: string;
  account_name: string;
  account_type: string | null;
  is_msa: string | null;
  parent_id: string | null;
  parent_name: string | null;
  site_count: number;
  active_site_count: number;
  likely_duplicate_of: string | null;
  likely_duplicate_name: string | null;
};

/** "58 sites with us" / "1 site with us" — the number that makes the parent obvious. */
export function siteCountLabel(n: number): string {
  return n === 1 ? '1 site with us' : `${n} sites with us`;
}

/** MSA reads as words, never as a raw Yes/No flag (TCSD-0103). */
export function msaLabel(is_msa: string | null): string {
  return is_msa === 'Yes' ? 'MSA in place' : 'no MSA';
}

export function CustomerResultRow({
  match,
  selected,
  onPick,
}: {
  match: CustomerMatch;
  selected: boolean;
  onPick: (m: CustomerMatch) => void;
}) {
  const dup = match.likely_duplicate_of && match.likely_duplicate_name;
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(match)}
        className={`flex w-full items-center gap-3 border-b border-tcs-border px-3 py-2.5 text-left last:border-b-0 hover:bg-tcs-surface ${
          selected ? 'bg-tcs-surface shadow-[inset_3px_0_0_var(--tcs-text)]' : ''
        }`}
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-tcs-text">
            {match.account_name}
          </span>
          <span className="block truncate text-xs text-tcs-text-muted">
            {[match.account_type, msaLabel(match.is_msa), match.account_id]
              .filter(Boolean)
              .join(' · ')}
            {match.parent_name ? ` · rolls up to ${match.parent_name}` : ''}
          </span>
        </span>
        <span className="ml-auto shrink-0 text-right">
          <span
            className={`inline-block rounded border px-1.5 py-0.5 text-[11px] font-semibold ${
              dup
                ? 'border-st-warn-border bg-st-warn-bg text-st-warn-fg'
                : match.site_count > 1
                  ? 'border-rag-green/40 bg-rag-green/10 text-rag-green'
                  : 'border-tcs-border-strong bg-tcs-surface text-tcs-text-muted'
            }`}
          >
            {siteCountLabel(match.site_count)}
            {dup ? ` · looks like a duplicate of ${match.likely_duplicate_name}` : ''}
          </span>
        </span>
      </button>
    </li>
  );
}

/**
 * "Create a new customer" is the LAST ROW of the results, never a separate control.
 * It only becomes visible after the BDD has seen what already exists — that ordering
 * is the guardrail, and a tooltip cannot be one.
 */
export function CreateCustomerRow({ typed, onCreate }: { typed: string; onCreate: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onCreate}
        className="flex w-full items-center gap-2 border-t border-tcs-border px-3 py-2.5 text-left hover:bg-tcs-surface"
      >
        <Plus className="h-3.5 w-3.5 shrink-0 text-tcs-text-muted" />
        <span className="min-w-0">
          <span className="block truncate text-sm text-tcs-text-muted">
            None of these — create {typed.trim() ? `“${typed.trim()}”` : 'a new customer'}
          </span>
          <span className="block text-xs text-tcs-text-faint">
            You’ll be asked which parent company it rolls up to
          </span>
        </span>
      </button>
    </li>
  );
}

/** The inline warning when the hint fires. Flagging files a review; it never merges. */
export function DuplicateWarning({
  picked,
  onFlag,
  onDismiss,
  flagged,
}: {
  picked: CustomerMatch;
  onFlag: () => void;
  onDismiss: () => void;
  flagged: boolean;
}) {
  return (
    <div className="mb-4 rounded-md border border-rag-red/40 bg-rag-red/5 p-3 text-xs leading-relaxed text-tcs-text">
      <p className="m-0 flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rag-red" />
        <span>
          <b>{picked.account_name}</b> holds {siteCountLabel(picked.site_count)} and looks like it
          was meant to be <b>{picked.likely_duplicate_name}</b>. Picking{' '}
          {picked.likely_duplicate_name} instead keeps this site out of that pile.
        </span>
      </p>
      <p className="m-0 mt-2 pl-5">
        {flagged ? (
          <span className="font-semibold text-st-warn-fg">Flagged for merge review.</span>
        ) : (
          <>
            <button type="button" onClick={onFlag} className="font-semibold underline">
              Flag for merge
            </button>
            <span className="mx-2 text-tcs-text-faint">·</span>
            <button type="button" onClick={onDismiss} className="underline">
              Dismiss
            </button>
          </>
        )}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ review card */

export function ReviewRow({
  label,
  value,
  note,
  highlight,
}: {
  label: string;
  value: ReactNode;
  note?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex gap-3 border-b border-tcs-border px-3.5 py-2.5 text-sm last:border-b-0 ${
        highlight ? 'bg-rag-green/5' : ''
      }`}
    >
      <span className="w-36 shrink-0 pt-0.5 text-xs text-tcs-text-muted">{label}</span>
      <span className="min-w-0 font-medium text-tcs-text">
        {value}
        {note && <span className="mt-0.5 block text-xs font-normal text-tcs-text-muted">{note}</span>}
      </span>
    </div>
  );
}
